import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { effectiveUnitPrice } from "@/lib/pricing";

const CartContext = createContext(null);
const STORAGE_KEY = "blisson_cart_v2";
const WISH_KEY = "blisson_wishlist_v1";

const loadJSON = (key, fallback) => {
  try {
    if (typeof window === "undefined") return fallback;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadJSON(STORAGE_KEY, []));
  const [wishlist, setWishlist] = useState(() => loadJSON(WISH_KEY, []));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
  }, [items]);

  useEffect(() => {
    try {
      window.localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
    } catch (e) {}
  }, [wishlist]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          original_price: product.original_price,
          image: product.image,
          quantity: qty,
          tiers: product.tiers || [],
        },
      ];
    });
    setTimeout(() => setOpen(true), 60);
  };

  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeItem(product_id);
    setItems((prev) =>
      prev.map((i) => (i.product_id === product_id ? { ...i, quantity } : i))
    );
  };

  const removeItem = (product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  };

  const clear = () => setItems([]);

  const toggleWishlist = (productId) => {
    setWishlist((prev) =>
      prev.includes(productId) ? prev.filter((p) => p !== productId) : [...prev, productId]
    );
  };

  // Augment each item with computed effective unit price + line_total
  const augmentedItems = useMemo(() => {
    return items.map((i) => {
      const unit = effectiveUnitPrice(i.price, i.tiers, i.quantity);
      return {
        ...i,
        unit_price: unit,
        line_total: unit * i.quantity,
        saving: Math.max(0, (Number(i.price) - unit) * i.quantity),
      };
    });
  }, [items]);

  const { subtotal, count, savings } = useMemo(() => {
    const s = augmentedItems.reduce((acc, i) => acc + i.line_total, 0);
    const c = augmentedItems.reduce((acc, i) => acc + i.quantity, 0);
    const sv = augmentedItems.reduce((acc, i) => acc + i.saving, 0);
    return { subtotal: s, count: c, savings: sv };
  }, [augmentedItems]);

  return (
    <CartContext.Provider
      value={{
        items: augmentedItems,
        wishlist,
        open,
        setOpen,
        addItem,
        updateQty,
        removeItem,
        clear,
        toggleWishlist,
        subtotal,
        count,
        savings,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
